import React, { useState, useRef } from "react";
import { Input, Button, Space, Modal, Checkbox, Upload, Progress, notification, Tag } from "antd";
import { SearchOutlined, InboxOutlined, LoadingOutlined, CheckCircleOutlined } from "@ant-design/icons";
import type { InputRef, TableColumnsType } from "antd";
import type { FilterConfirmProps } from "antd/es/table/interface";
import { useEffect, useMemo } from "react";
import type { UploadFile, UploadProps } from "antd";
import axios from "axios";
import useSWR, { mutate } from "swr";
import * as XLSX from 'xlsx';
import { useAuth } from "../../context/AuthContext";

export interface DataType {
    id: number;
    date: string;
    code_doc: string;
    name_doc: string;
    warehouse: string;
    status: string;
    keterangan: string;
    success_count?: number;
    failed_count?: number;
    total_count?: number;
}

type DataIndex = keyof DataType;
const fetcher = (url: string) => axios.get(url).then(res => res.data);
const BROWSER_DATA_API_URL = `${process.env.REACT_APP_BACKEND_URL}/api/spareparts/browser-data`;

export const withStatusImportSparepartsMaster = (WrappedComponent: React.ComponentType<any>) => {
    return (props: any) => {
        const { userData } = useAuth();
        const [searchText, setSearchText] = useState("");
        const [searchedColumn, setSearchedColumn] = useState("");
        const searchInput = useRef<InputRef>(null);
        const handleDownloadReport = async (codeDoc: string, type: string) => {
            try {
                const fileName = `${codeDoc}-${type}.xlsx`;
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/reports/${fileName}`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                notification.error({ message: 'Error', description: "Gagal mengunduh file laporan", duration: 4 });
            }
        };
        const handleDownloadOriginalFile = async (fileName: string) => {
            try {
                const response = await axios.get(`${process.env.REACT_APP_BACKEND_URL}/api/uploads/upload_spareparts/${fileName}`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                notification.error({ message: 'Error', description: "Gagal mengunduh file dokumen", duration: 4 });
            }
        };
        const [isUpdateModalVisible, setIsUpdateModalVisible] = useState(false);
        const [fileList, setFileList] = useState<UploadFile[]>([]);
        const [isUploading, setIsUploading] = useState(false);
        const [uploadProgress, setUploadProgress] = useState(0);
        const [selectedUpdateColumns, setSelectedUpdateColumns] = useState<string[]>([]);
        const [disabledFields, setDisabledFields] = useState<string[]>([]);
        const [isNewModalVisible, setIsNewModalVisible] = useState(false);
        const [selectedNewColumns, setSelectedNewColumns] = useState<string[]>([]);
        const [isDownloadingTemplate, setIsDownloadingTemplate] = useState(false);
        const [newFileList, setNewFileList] = useState<UploadFile[]>([]);

        const handleDownloadTemplate = async (fileName: string) => {
            setIsDownloadingTemplate(true);
            try {
                const response = await axios.get(`/reports/${fileName}`, { responseType: 'blob' });
                const url = window.URL.createObjectURL(new Blob([response.data]));
                const link = document.createElement('a');
                link.href = url;
                link.setAttribute('download', fileName);
                document.body.appendChild(link);
                link.click();
                link.remove();
            } catch (error) {
                notification.error({ message: 'Error', description: "Gagal mengunduh file template", duration: 4 });
            } finally {
                setIsDownloadingTemplate(false);
            }
        };

        const showUpdateModal = () => {
            setIsUpdateModalVisible(true);
        };

        const showNewModal = () => {
            setIsNewModalVisible(true);
        };

        const handleUpdateModalCancel = () => {
            if (isUploading) return;
            setIsUpdateModalVisible(false);
            setFileList([]);
            setUploadProgress(0);
            setSelectedUpdateColumns([]);
        };

        const handleNewModalCancel = () => {
            setIsNewModalVisible(false);
            setSelectedNewColumns([]);
            setNewFileList([]);
        };

        const [isRemarkModalOpen, setIsRemarkModalOpen] = useState(false);
        const [modalRemark, setModalRemark] = useState("");

        const remarkPrefix = useMemo(() => {
            const labelMap: { [key: string]: string } = {
                item_name: 'Item Name',
                brand: 'Brand',
                item_class: 'Item Class',
                hpp: 'HPP',
                price: 'Price',
                uom: 'UoM',
                uom_rate: 'UoM Rate'
            };
            return `Perubahan ${selectedUpdateColumns.map(col => `{${labelMap[col] || col}}`).join(', ')}`;
        }, [selectedUpdateColumns]);

        const handleUpdateProcess = () => {
            if (selectedUpdateColumns.length === 0) {
                notification.error({ message: 'Error', description: "Pilih setidaknya satu kolom untuk diperbarui.", duration: 4 });
                return;
            }
            if (fileList.length === 0) {
                notification.error({ message: 'Error', description: "Tidak ada file yang dipilih untuk diproses.", duration: 4 });
                return;
            }
            setModalRemark(remarkPrefix);
            setIsRemarkModalOpen(true);
        };

        const handleActualUpdateProcess = async () => {
            if (fileList.length > 0 && fileList[0]) {
                const fileToProcess = fileList[0].originFileObj || fileList[0];
                setIsUploading(true);
                setUploadProgress(0);
                const formData = new FormData();
                formData.append('file', fileToProcess as File);
                formData.append('columnsToUpdate', JSON.stringify(selectedUpdateColumns));
                formData.append('modal_remark', modalRemark);
                formData.append('kode_grup1', userData?.kode_grup1 || '');
                const startTime = Date.now();
                try {
                    const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/spareparts/update-from-excel`, formData, {
                        headers: { 'Content-Type': 'multipart/form-data' },
                        onUploadProgress: (progressEvent) => {
                            const total = progressEvent.total ?? fileToProcess.size;
                            if (total) {
                                const percentCompleted = Math.round((progressEvent.loaded * 100) / total);
                                setUploadProgress(percentCompleted);
                            }
                        },
                    });
                    notification.success({ message: 'Success', description: response.data.message || 'Proses pembaruan selesai.', duration: 4 });
                    setIsRemarkModalOpen(false);
                    setIsUpdateModalVisible(false);
                    setFileList([]);
                    setModalRemark("");
                    if (response.data.failures && response.data.failures.length > 0) {
                        notification.warning({ message: 'Warning', description: `Ditemukan ${response.data.failureCount} data gagal. File laporan kegagalan sedang dibuat...`, duration: 4 });
                        const failures = response.data.failures;
                        const header = [
                            "part no", "item name", "brand", "item class", "hpp",
                            "price", "uom", "uom_rate", "location rack", "Alasan Kegagalan"
                        ];
                        const dataToExport = failures.map((row: any) => [
                            row.part_no, row.item_name, row.brand, row.item_class, row.hpp,
                            row.price, row.uom, row.uom_rate, row.location_rack,
                            row.error_reason
                        ]);
                        const ws = XLSX.utils.aoa_to_sheet([header, ...dataToExport]);
                        const wb = XLSX.utils.book_new();
                        XLSX.utils.book_append_sheet(wb, ws, "Gagal Update");
                        const colWidths = header.map((_, i) => ({
                            wch: Math.max(header[i].length, ...dataToExport.map((row: any[]) => row[i] ? String(row[i]).length : 0)) + 2
                        }));
                        ws['!cols'] = colWidths;
                        XLSX.writeFile(wb, "update_failed.xlsx");
                    }
                    mutate(BROWSER_DATA_API_URL);
                    mutate(`${process.env.REACT_APP_BACKEND_URL}/api/status-import-spareparts-master`);
                } catch (error: any) {
                    console.error("Update error:", error);
                    const errorMessage = error.response?.data?.error || 'Gagal memperbarui data dari file. Periksa konsol untuk detail.';
                    notification.error({ message: 'Error', description: errorMessage, duration: 4 });
                } finally {
                    const elapsedTime = Date.now() - startTime;
                    const minDuration = 5000;
                    const remainingTime = minDuration - elapsedTime;
                    if (remainingTime > 0) {
                        await new Promise(resolve => setTimeout(resolve, remainingTime));
                    }
                    setIsUploading(false);
                    setUploadProgress(100);
                }
            } else {
                notification.error({ message: 'Error', description: "Tidak ada file yang dipilih untuk diproses.", duration: 4 });
            }
        };

        const uploadProps: UploadProps = {
            name: 'file',
            multiple: false,
            accept: ".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            onRemove: () => {
                setFileList([]);
            },
            beforeUpload: file => {
                const isXlsx = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.name.endsWith('.xlsx');
                if (!isXlsx) {
                    notification.error({ message: 'Error', description: `${file.name} bukan file .xlsx`, duration: 4 });
                } else {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        try {
                            const data = new Uint8Array(e.target?.result as ArrayBuffer);
                            const workbook = XLSX.read(data, { type: 'array' });
                            const sheetName = workbook.SheetNames[0];
                            const worksheet = workbook.Sheets[sheetName];
                            const rows = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1 });
                            const isLessColumns = !rows[0] || rows[0].length < 7;
                            if (isLessColumns) {
                                notification.error({ message: 'Error', description: 'Invalid file (less_columns)', duration: 4 });
                                setFileList([]);
                            } else {
                                setFileList([file]);
                            }
                        } catch (err) {
                            notification.error({ message: 'Error', description: 'Gagal membaca file Excel', duration: 4 });
                        }
                    };
                    reader.readAsArrayBuffer(file);
                }
                return false;
            },
            fileList,
            disabled: isUploading,
        };

        const newUploadProps: UploadProps = {
            name: 'file',
            multiple: false,
            accept: ".xlsx, application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
            onRemove: () => {
                setNewFileList([]);
            },
            beforeUpload: file => {
                const isXlsx = file.type === 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' || file.name.endsWith('.xlsx');
                if (!isXlsx) {
                    notification.error({ message: 'Error', description: `${file.name} bukan file .xlsx`, duration: 4 });
                } else {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        try {
                            const data = new Uint8Array(e.target?.result as ArrayBuffer);
                            const workbook = XLSX.read(data, { type: 'array' });
                            const sheetName = workbook.SheetNames[0];
                            const worksheet = workbook.Sheets[sheetName];
                            const rows = XLSX.utils.sheet_to_json<any[]>(worksheet, { header: 1 });
                            const isOverColumns = rows.some(row => row && row.length > 7);
                            if (isOverColumns) {
                                notification.error({ message: 'Error', description: 'Invalid file (over_columns_6)', duration: 4 });
                                setNewFileList([]);
                            } else {
                                setNewFileList([file]);

                            }
                        } catch (err) {
                            notification.error({ message: 'Error', description: 'Gagal membaca file Excel', duration: 4 });
                        }
                    };
                    reader.readAsArrayBuffer(file);
                }
                return false;
            },
            fileList: newFileList,
            disabled: isUploading,
        };

        const handleNewProcess = async () => {
            if (newFileList.length === 0) {
                notification.error({ message: 'Error', description: "Tidak ada file yang dipilih untuk diproses.", duration: 4 });
                return;
            }
            const fileToProcess = newFileList[0].originFileObj || newFileList[0];
            setIsUploading(true);
            setUploadProgress(0);
            const formData = new FormData();
            formData.append('file', fileToProcess as File);
            const startTime = Date.now();
            try {
                const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/spareparts/import`, formData, {
                    headers: { 'Content-Type': 'multipart/form-data' },
                    onUploadProgress: (progressEvent) => {
                        const total = progressEvent.total ?? fileToProcess.size;
                        if (total) {
                            const percentCompleted = Math.round((progressEvent.loaded * 100) / total);
                            setUploadProgress(percentCompleted);
                        }
                    },
                });
                notification.success({
                    message: 'Success',
                    description: response.data.message || 'Proses import data baru selesai.',
                    duration: 4
                });
                setIsNewModalVisible(false);
                setNewFileList([]);
                mutate(`${process.env.REACT_APP_BACKEND_URL}/api/status-import-spareparts-master`);
            } catch (error: any) {
                console.error("Import error:", error);
                const errorMessage = error.response?.data?.error || 'Gagal mengimpor data baru dari file.';
                notification.error({ message: 'Error', description: errorMessage, duration: 4 });
            } finally {
                const elapsedTime = Date.now() - startTime;
                const minDuration = 3000;
                const remainingTime = minDuration - elapsedTime;
                if (remainingTime > 0) {
                    await new Promise(resolve => setTimeout(resolve, remainingTime));
                }
                setIsUploading(false);
                setUploadProgress(100);
            }
        };

        const { data: settingsData, error: settingsError } = useSWR<{ list_check: string[] | string }>(
            `${process.env.REACT_APP_BACKEND_URL}/api/settings/update-item-part`,
            fetcher
        );

        useEffect(() => {
            if (settingsData && settingsData.list_check) {
                const valueToFieldMap: { [key: string]: string } = {
                    '1': 'item_name',
                    '2': 'brand',
                    '3': 'item_class',
                    '5': 'hpp',
                    '6': 'price',
                    '7': 'uom',
                    '8': 'uom_rate',
                };
                let checkList: string[] = [];
                if (Array.isArray(settingsData.list_check)) {
                    checkList = settingsData.list_check;
                } else if (typeof settingsData.list_check === 'string' && settingsData.list_check.startsWith('{')) {
                    checkList = settingsData.list_check.replace(/[{}"']/g, '').split(',').filter(Boolean);
                }
                const disabled = checkList.map(value => valueToFieldMap[value.trim()]).filter(Boolean);
                setDisabledFields(disabled);
            }
            if (settingsError) {
                notification.error({ message: 'Error', description: "Gagal memuat pengaturan update item.", duration: 4 });
                console.error("Error fetching settings:", settingsError);
            }
        }, [settingsData, settingsError]);

        const updateColumnOptions = useMemo(() => [
            { label: 'Item Name', value: 'item_name' },
            { label: 'Brand', value: 'brand' },
            { label: 'Item Class', value: 'item_class' },
            { label: 'HPP', value: 'hpp' },
            { label: 'Price', value: 'price' },
            { label: 'UoM', value: 'uom' },
            { label: 'UoM Rate', value: 'uom_rate' },
        ].map(option => ({
            ...option,
            disabled: disabledFields.includes(option.value),
        })), [disabledFields]);

        const handleSearch = (
            selectedKeys: string[],
            confirm: (param?: FilterConfirmProps) => void,
            dataIndex: DataIndex
        ) => {
            confirm();
            setSearchText(selectedKeys[0]);
            setSearchedColumn(dataIndex);
        };

        const handleReset = (clearFilters: () => void) => {
            clearFilters();
            setSearchText("");
        };

        const getColumnSearchProps = (dataIndex: DataIndex, title: string) => ({
            filterDropdown: ({ setSelectedKeys, selectedKeys, confirm, clearFilters, close }: any) => (
                <div style={{ padding: 8 }} onKeyDown={(e) => e.stopPropagation()}>
                    <Input
                        ref={searchInput}
                        placeholder={`Search ${title}`}
                        value={selectedKeys[0]}
                        onChange={(e) => setSelectedKeys(e.target.value ? [e.target.value] : [])}
                        onPressEnter={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                        style={{ marginBottom: 8, display: "block" }}
                    />
                    <Space>
                        <Button
                            type="primary"
                            onClick={() => handleSearch(selectedKeys as string[], confirm, dataIndex)}
                            icon={<SearchOutlined />}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Search
                        </Button>
                        <Button
                            onClick={() => clearFilters && handleReset(clearFilters)}
                            size="small"
                            style={{ width: 90 }}
                        >
                            Reset
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => {
                                confirm({ closeDropdown: false });
                                setSearchText((selectedKeys as string[])[0]);
                                setSearchedColumn(dataIndex);
                            }}
                        >
                            Filter
                        </Button>
                        <Button
                            type="link"
                            size="small"
                            onClick={() => {
                                close();
                            }}
                        >
                            close
                        </Button>
                    </Space>
                </div>
            ),
            filterIcon: (filtered: boolean) => (
                <SearchOutlined style={{ color: filtered ? "#1677ff" : undefined }} />
            ),
            onFilter: (value: any, record: any) =>
                record[dataIndex]
                    ? record[dataIndex]
                        .toString()
                        .toLowerCase()
                        .includes((value as string).toLowerCase())
                    : false,
            onFilterDropdownOpenChange: (visible: boolean) => {
                if (visible) {
                    setTimeout(() => searchInput.current?.select(), 100);
                }
            },
        });

        const handleStatusAction = (id: number, type: 'approve' | 'cancel') => {
            const endpoint = type === 'approve' ? 'approve-import' : 'cancel-import';
            Modal.confirm({
                title: 'Konfirmasi',
                content: `Apakah Anda yakin ingin ${type === 'approve' ? 'memproses' : 'membatalkan'} data ini?`,
                onOk: async () => {
                    try {
                        if (type === 'approve') {
                            axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/spareparts/${endpoint}`, { id })
                                .then((response) => {
                                    notification.success({ message: 'Success', description: response.data.message, duration: 4 });
                                    mutate(`${process.env.REACT_APP_BACKEND_URL}/api/status-import-spareparts-master`);
                                })
                                .catch((error) => {
                                    notification.error({ message: 'Error', description: error.response?.data?.error || 'Terjadi kesalahan saat memproses', duration: 4 });
                                    mutate(`${process.env.REACT_APP_BACKEND_URL}/api/status-import-spareparts-master`);
                                });
                            notification.info({ message: 'Info', description: "Proses approval sedang dijalankan di latar belakang server.", duration: 4 });
                            mutate(`${process.env.REACT_APP_BACKEND_URL}/api/status-import-spareparts-master`);
                        } else {
                            const response = await axios.post(`${process.env.REACT_APP_BACKEND_URL}/api/spareparts/${endpoint}`, { id });
                            notification.success({ message: 'Success', description: response.data.message, duration: 4 });
                            mutate(`${process.env.REACT_APP_BACKEND_URL}/api/status-import-spareparts-master`);
                        }
                    } catch (error: any) {
                        notification.error({ message: 'Error', description: error.response?.data?.error || 'Terjadi kesalahan', duration: 4 });
                    }
                }
            });
        };
        const columns: TableColumnsType<DataType> = [
            {
                title: "Date Upload",
                dataIndex: "date",
                key: "date",
                sorter: (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime(),
                ...getColumnSearchProps("date" as any, "Date Upload"),
                render: (text: string) => {
                    if (!text) return "-";
                    const d = new Date(text);
                    const day = String(d.getDate()).padStart(2, '0');
                    const month = String(d.getMonth() + 1).padStart(2, '0');
                    const year = d.getFullYear();
                    const hours = String(d.getHours()).padStart(2, '0');
                    const minutes = String(d.getMinutes()).padStart(2, '0');
                    const seconds = String(d.getSeconds()).padStart(2, '0');
                    return `${day}-${month}-${year} ${hours}:${minutes}:${seconds}`;
                }
            },
            {
                title: "Kode Dokumen",
                dataIndex: "code_doc",
                key: "code_doc",
                sorter: (a, b) => a.code_doc.localeCompare(b.code_doc),
                ...getColumnSearchProps("code_doc", "Kode Dokumen"),
            },
            {
                title: "Nama Dokumen",
                dataIndex: "name_doc",
                key: "name_doc",
                sorter: (a, b) => a.name_doc.localeCompare(b.name_doc),
                ...getColumnSearchProps("name_doc", "Nama Dokumen"),
                render: (text: string) => (
                    <Button type="link" onClick={() => handleDownloadOriginalFile(text)} style={{ padding: 0 }}>{text}</Button>
                ),
            },
            {
                title: "Warehouse",
                dataIndex: "warehouse",
                key: "warehouse",
                sorter: (a, b) => a.warehouse.localeCompare(b.warehouse),
                ...getColumnSearchProps("warehouse", "Warehouse"),
            },
            {
                title: "Status",
                dataIndex: "status",
                key: "status",
                sorter: (a, b) => a.status.localeCompare(b.status),
                ...getColumnSearchProps("status", "Status"),
                render: (status: string) => {
                    const labels: Record<string, string> = { '5': 'Cancel', '2': 'Approved', '3': 'Waiting for Approved' };
                    const label = labels[status] || status;
                    if (status === 'Processing') {
                        return (
                            <Space>
                                <span>Processing</span>
                                <LoadingOutlined style={{ color: '#1890ff' }} />
                            </Space>
                        );
                    }
                    if (status === '2') {
                        return (
                            <Space>
                                <span>Approved</span>
                                <CheckCircleOutlined style={{ color: '#52c41a' }} />
                            </Space>
                        );
                    }
                    return label;
                }
            },
            {
                title: "Permintaan Perubahan",
                dataIndex: "keterangan",
                key: "keterangan",
                sorter: (a, b) => a.keterangan.localeCompare(b.keterangan),
                ...getColumnSearchProps("keterangan", "Keterangan"),
            },
            {
                title: "Keterangan",
                key: "download_reports",
                render: (_, record) => (
                    <Space>
                        <Tag color="green" style={{ cursor: 'pointer' }} onClick={() => handleDownloadReport(record.code_doc, 'success')}>Success {record.success_count ?? 0}</Tag>
                        <Tag color="red" style={{ cursor: 'pointer' }} onClick={() => handleDownloadReport(record.code_doc, 'failed')}>Failed {record.failed_count ?? 0}</Tag>
                        <Tag color="orange" style={{ cursor: 'pointer' }} onClick={() => handleDownloadReport(record.code_doc, 'before')}>Before</Tag>
                        <Tag color="blue" style={{ cursor: 'pointer' }} onClick={() => handleDownloadReport(record.code_doc, 'total')}>Total {record.total_count ?? 0}</Tag>
                    </Space>
                )
            },
        ];

        const { data: statusImportData } = useSWR<DataType[]>(
            `${process.env.REACT_APP_BACKEND_URL}/api/status-import-spareparts-master`,
            fetcher,
            { refreshInterval: 3000 }
        );
        const dataSource = statusImportData || [];

        return (
            <>
                <WrappedComponent
                    {...props}
                    columns={columns}
                    dataSource={dataSource}
                    showUpdateModal={showUpdateModal}
                    isUpdateModalVisible={isUpdateModalVisible}
                    handleUpdateModalCancel={handleUpdateModalCancel}
                    handleUpdateProcess={handleUpdateProcess}
                    uploadProps={uploadProps}
                    isUploading={isUploading}
                    uploadProgress={uploadProgress}
                    selectedUpdateColumns={selectedUpdateColumns}
                    setSelectedUpdateColumns={setSelectedUpdateColumns}
                    updateColumnOptions={updateColumnOptions}
                    fileList={fileList}
                    showNewModal={showNewModal}
                    isNewModalVisible={isNewModalVisible}
                    handleNewModalCancel={handleNewModalCancel}
                    selectedNewColumns={selectedNewColumns}
                    setSelectedNewColumns={setSelectedNewColumns}
                    isDownloadingTemplate={isDownloadingTemplate}
                    handleDownloadTemplate={handleDownloadTemplate}
                    newFileList={newFileList}
                    newUploadProps={newUploadProps}
                    handleNewProcess={handleNewProcess}
                />
                <Modal
                    title="Remark"
                    open={isRemarkModalOpen}
                    onCancel={() => setIsRemarkModalOpen(false)}
                    onOk={handleActualUpdateProcess}
                    confirmLoading={isUploading}
                    destroyOnClose
                    className="modal_remark_proses"
                >
                    <Input.TextArea
                        rows={3}
                        placeholder="Remark"
                        value={modalRemark}
                        onChange={(e) => {
                            const val = e.target.value;
                            if (val.startsWith(remarkPrefix)) {
                                setModalRemark(val);
                            } else {
                                setModalRemark(remarkPrefix);
                            }
                        }}
                    />
                </Modal>
            </>
        );
    };
};